import {ChangeEvent, useMemo} from 'react';
import {useTranslation} from 'react-i18next';
import {
  Control,
  UseFormWatch,
  UseFormSetValue,
  Controller,
} from 'react-hook-form';
import {SVG} from '../../../../../../constants/assetLists';
import AppSwitch from '../../../../../../components/common/AppSwitch';
import AppMUIAutocomplete from '../../../../../../components/common/AppMUIAutoComplete';
import {
  FormDataType,
  Question,
  QuestionType,
  QUESTION_TYPE_OPTIONS,
} from '../../types';
import MultipleChoiceOptions from './QuestionTypes/MultipleChoiceOptions';
import CheckboxOptions from './QuestionTypes/CheckboxOptions';
import DropdownOptions from './QuestionTypes/DropdownOptions';
import ShortAnswerField from './QuestionTypes/ShortAnswerField';
import LinearScaleField from './QuestionTypes/LinearScaleField';
import RatingField from './QuestionTypes/RatingField';
import DateField from './QuestionTypes/DateField';
import TimeField from './QuestionTypes/TimeField';
import AppTextInput from '../../../../../../components/common/AppTextInput';
import AppFileInputButton from '../../../../../../components/common/AppFileInputButton';
import FilePreview from './FilePreview';
import UploadFile from './QuestionTypes/UploadFile';
import RangeField from './QuestionTypes/RangeField';

type Props = {
  index: number;
  control: Control<FormDataType>;
  watch: UseFormWatch<FormDataType>;
  setValue: UseFormSetValue<FormDataType>;
  onClone: () => void;
  onRemove: () => void;
  onMandatoryChange: (checked: ChangeEvent<HTMLInputElement>) => void;
  errors: any;
};

function QuestionItem({
  index,
  control,
  watch,
  setValue,
  onClone,
  onRemove,
  onMandatoryChange,
  errors,
}: Props) {
  const {t, i18n} = useTranslation();
  const isHebrew = i18n.language === 'heb';

  const question = watch(`questions.${index}`) as Question;

  const questionTypeOptions = useMemo(
    () =>
      QUESTION_TYPE_OPTIONS.map(opt => ({
        value: opt.type,
        label: isHebrew ? opt.labelHe : opt.labelEn,
        icon: SVG[opt.svgKey as keyof typeof SVG] as string,
      })),
    [isHebrew],
  );

  const selectedTypeOption = questionTypeOptions.find(
    opt => opt.value === question.type,
  );

  const questionFile = question.image;

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setValue(`questions.${index}.image` as any, file, {shouldDirty: true});
    }
    e.target.value = '';
  };

  const handleImageRemove = () => {
    setValue(`questions.${index}.image` as any, undefined, {
      shouldDirty: true,
    });
  };

  const handleTypeChange = (newType: QuestionType) => {
    // Keep common fields, reset type-specific fields
    setValue(`questions.${index}.type` as any, newType, {shouldDirty: true});

    // Reset type-specific fields based on new type
    switch (newType) {
      case 'multiple-choice':
        setValue(`questions.${index}.options` as any, [
          {id: `${question.id}-opt-1`, title: '', image: undefined},
          {id: `${question.id}-opt-2`, title: '', image: undefined},
        ]);
        setValue(`questions.${index}.hasOtherOption` as any, false);
        setValue(`questions.${index}.otherOptionImage` as any, undefined);
        break;
      case 'checkbox':
        setValue(`questions.${index}.options` as any, [
          {id: `${question.id}-opt-1`, title: '', image: undefined},
          {id: `${question.id}-opt-2`, title: '', image: undefined},
        ]);
        setValue(`questions.${index}.hasOtherOption` as any, false);
        setValue(`questions.${index}.otherOptionImage` as any, undefined);
        break;
      case 'dropdown':
        setValue(`questions.${index}.options` as any, [
          {id: `${question.id}-opt-1`, title: ''},
          {id: `${question.id}-opt-2`, title: ''},
        ]);
        setValue(`questions.${index}.hasOtherOption` as any, false);
        setValue(`questions.${index}.otherOptionImage` as any, undefined);
        break;
      case 'linear-scale':
        setValue(`questions.${index}.fromValue` as any, 1);
        setValue(`questions.${index}.toValue` as any, 5);
        break;
      case 'rating':
        setValue(`questions.${index}.maxRating` as any, 5);
        setValue(`questions.${index}.icon` as any, 'star');
        break;
      case 'date':
        setValue(`questions.${index}.dateType` as any, 'single');
        break;
      case 'time':
        setValue(`questions.${index}.timeType` as any, 'single');
        break;
      case 'file-upload':
        setValue(`questions.${index}.fileUploadConfig` as any, {
          maxFileSizeMB: 10,
          allowedTypes: [],
          maxFiles: 1,
        });
        break;
      case 'range':
        setValue(`questions.${index}.rangeConfig` as any, {
          rangeType: 'numeric',
          form: 'between',
          min: 0,
          max: 100,
          minLabel: '',
          maxLabel: '',
          unit: '',
          canUploadImage: false,
        });
        break;
    }
  };

  const renderQuestionTypeFields = () => {
    switch (question.type) {
      case 'short-answer':
        return <ShortAnswerField />;
      case 'multiple-choice':
        return (
          <MultipleChoiceOptions
            index={index}
            control={control}
            watch={watch}
            setValue={setValue}
            errors={errors}
          />
        );
      case 'checkbox':
        return (
          <CheckboxOptions
            index={index}
            control={control}
            watch={watch}
            setValue={setValue}
            errors={errors}
          />
        );
      case 'dropdown':
        return (
          <DropdownOptions
            index={index}
            control={control}
            watch={watch}
            setValue={setValue}
            errors={errors}
          />
        );
      case 'linear-scale':
        return (
          <LinearScaleField
            index={index}
            control={control}
            watch={watch}
            setValue={setValue}
          />
        );
      case 'rating':
        return (
          <RatingField
            index={index}
            control={control}
            watch={watch}
            setValue={setValue}
          />
        );
      case 'date':
        return (
          <DateField
            index={index}
            control={control}
            watch={watch}
            setValue={setValue}
          />
        );
      case 'time':
        return (
          <TimeField
            index={index}
            control={control}
            watch={watch}
            setValue={setValue}
          />
        );
      case 'file-upload':
        return (
          <UploadFile
            index={index}
            control={control}
            watch={watch}
            setValue={setValue}
            errors={errors}
          />
        );
      case 'range':
        return (
          <RangeField
            index={index}
            control={control}
            watch={watch}
            setValue={setValue}
            errors={errors}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="rounded-lg shadow-md p-3 border border-gray70">
      {/* Question Header */}
      <div className="flex items-center gap-3 mb-4">
        {/* Question Title Input */}
        <div className="flex-1">
          <Controller
            name={`questions.${index}.title`}
            control={control}
            render={({field}) => (
              <AppTextInput
                placeholder={t('forms.dialog.questionTitle')}
                className="border-none"
                value={field.value}
                onChange={field.onChange}
                error={errors?.title?.message}
              />
            )}
          />
        </div>

        {/* Image Upload Button */}
        <AppFileInputButton
          id={`questions.${index}.image-upload`}
          icon={SVG.imageIcon}
          accept="image/*"
          onChange={handleImageChange}
        />

        {/* Question Type Selector */}
        <div className="w-[255px]">
          <AppMUIAutocomplete
            size="small"
            options={questionTypeOptions}
            value={
              questionTypeOptions.find(opt => opt.value === question.type) ||
              undefined
            }
            getOptionLabel={option => option.label}
            onChange={(_, newValue) => {
              if (newValue) {
                handleTypeChange(newValue.value as QuestionType);
              }
            }}
            disableClearable
            renderOption={(props, option) => (
              <li {...props} key={option.value}>
                <img
                  src={option.icon}
                  alt=""
                  className="w-[18px] h-[18px] object-contain"
                  style={{marginInlineEnd: 12}}
                />
                {option.label}
              </li>
            )}
            startAdornment={
              selectedTypeOption ? (
                <img
                  src={selectedTypeOption.icon}
                  alt=""
                  className="w-[18px] h-[18px] object-contain"
                  style={{marginInlineEnd: 8}}
                />
              ) : undefined
            }
          />
        </div>
      </div>

      {/* Question Type Specific Fields */}
      <div className="mb-4">{renderQuestionTypeFields()}</div>

      {/* Question File Preview */}
      {questionFile && (
        <div className="mb-4 border-top pt-4 border-gray70">
          <FilePreview file={questionFile} onRemove={handleImageRemove} />
        </div>
      )}

      {/* Question Footer */}
      <div className="flex items-center gap-3 px-3 py-2 bg-gray95">
        <div className="flex items-center gap-2">
          <Controller
            name={`questions.${index}.mandatory`}
            control={control}
            render={({field}) => (
              <AppSwitch
                checked={field.value}
                onChange={checked => {
                  field.onChange(checked);
                  onMandatoryChange?.(checked); // notify parent
                }}
              />
            )}
          />
          <span className="text-[16px] font-semibold text-black">
            {t('forms.dialog.mandatory')}
          </span>
        </div>

        <button type="button" onClick={onRemove}>
          <img
            src={SVG.listDeleteIcon}
            alt="delete"
            className="w-[16px] h-[16px] object-contain"
          />
        </button>

        <button type="button" onClick={onClone}>
          <img
            src={SVG.cloneDarkIcon}
            alt="clone"
            className="w-[16px] h-[16px] object-contain"
          />
        </button>
      </div>
    </div>
  );
}

export default QuestionItem;
